import { useMemo } from 'react'
import { Box, HStack, Text } from '@chakra-ui/react'
import dayjs from 'dayjs'
import type { CalendarEvent } from '../mock-data'
import { buildCalendarBlocks, CalendarViewMode, DATE_KEY, HOUR_HEIGHT, timelineHours } from '../calendar-layout'
import CalendarEventCard from './calendar-event-card'
import CalendarViewControls from './calendar-view-controls'
import CalendarDayStrip from './calendar-day-strip'
import CalendarMonthGrid from './calendar-month-grid'

// The dashboard's calendar, in three modes.
//
// Day and Week both render one day's timeline — the difference is the strip above
// it, which is the whole Mon–Sun row in Week and a single day with chevrons in Day.
// Month hands over to the grid. That is the same division the player's mobile
// schedule makes, and it is why switching between Day and Week needs no new
// request: both are looking at the same week.
//
// Concurrent events share their row as side-by-side lanes, so each card still spans
// the hours its own event runs.
//
// The mode and the selected date are owned by the page, not the panel: the page
// fetches the window they imply, and the panel unmounts under the page spinner
// while a new Organization/Team loads — state kept here would snap back to
// Day / today every time the selection changed.

interface ICalendarPanelProps {
  events: CalendarEvent[]
  mode: CalendarViewMode
  selectedDate: Date
  onModeChange: (mode: CalendarViewMode) => void
  onSelectedDateChange: (date: Date) => void
}

const GUTTER_WIDTH = 60
const GUTTER_GAP = 12
/** Where the cards start: past the hour labels and their gap. */
const CONTENT_LEFT = GUTTER_WIDTH + GUTTER_GAP
/** Keeps two side-by-side cards from reading as one wide card. */
const LANE_GAP = 6
const HOUR_LINE = '1px solid rgba(255,255,255,0.06)'
/** Breathing room under the lowest card so it does not sit flush on the border. */
const BLOCK_BOTTOM_GAP = 8

const formatHour = (hour: number): string => {
  const period = hour >= 12 ? 'PM' : 'AM'
  const display = hour % 12 === 0 ? 12 : hour % 12
  return `${display} ${period}`
}

const CalendarPanel = ({
  events,
  mode,
  selectedDate,
  onModeChange: setMode,
  onSelectedDateChange: setSelectedDate
}: ICalendarPanelProps) => {
  const selectedIso = dayjs(selectedDate).format(DATE_KEY)
  const datesWithEvents = useMemo(() => new Set(events.map(event => event.date)), [events])
  const dayEvents = useMemo(() => events.filter(event => event.date === selectedIso), [events, selectedIso])

  // Hour rows are sized to the day on screen. Sizing them to the whole fetched
  // range would let one late event in another week stretch every day's grid.
  const hours = useMemo(() => timelineHours(dayEvents), [dayEvents])
  const blocks = useMemo(() => buildCalendarBlocks(dayEvents, hours), [dayEvents, hours])
  const timelineHeight = useMemo(
    () => Math.max(hours.length * HOUR_HEIGHT, ...blocks.map(block => block.top + block.height + BLOCK_BOTTOM_GAP)),
    [blocks, hours]
  )

  const handleSelectDayFromMonth = (date: Date) => {
    setSelectedDate(date)
    setMode('day')
  }

  return (
    <Box bg="rgba(27, 42, 63, 0.60)" border="1px solid rgba(194, 199, 214, 0.10)" borderRadius="16px" p="16px">
      <HStack justify="space-between" align="center" mb="14px">
        <Text color="white" fontSize="18px" fontWeight={700} m={0}>
          Schedule
        </Text>
        <CalendarViewControls mode={mode} onModeChange={setMode} onToday={() => setSelectedDate(new Date())} />
      </HStack>

      {mode === 'month' ? (
        <CalendarMonthGrid
          events={events}
          selectedDate={selectedDate}
          onSelectDay={handleSelectDayFromMonth}
          onChangeMonth={setSelectedDate}
        />
      ) : (
        <>
          <CalendarDayStrip
            mode={mode}
            selectedDate={selectedDate}
            onSelectDate={setSelectedDate}
            datesWithEvents={datesWithEvents}
          />

          {/* Absolutely positioned blocks, not grid rows: a card has to start at
              the minute its event does, and stacked cards have to be able to sit
              at arbitrary offsets inside an hour.

              The height covers the hour rows *or* the lowest card, whichever runs
              further, so an event in the last hour is never clipped by the
              container's edge. */}
          <Box position="relative" height={`${timelineHeight}px`}>
            {hours.map((hour, index) => (
              <Box
                key={hour}
                position="absolute"
                top={`${index * HOUR_HEIGHT}px`}
                left={0}
                right={0}
                height={`${HOUR_HEIGHT}px`}
              >
                <Box position="absolute" top={0} left={`${GUTTER_WIDTH}px`} right={0} borderTop={HOUR_LINE} />
                <Text
                  position="absolute"
                  top="-8px"
                  left={0}
                  width={`${GUTTER_WIDTH - 8}px`}
                  textAlign="right"
                  color="rgba(255,255,255,0.55)"
                  fontSize="11px"
                  m={0}
                >
                  {formatHour(hour)}
                </Text>
              </Box>
            ))}

            {blocks.map(block => {
              // Percentages so the split stays fluid as the panel resizes; the
              // gutter is subtracted first because the lanes divide the content
              // area, not the whole row.
              const laneWidth = 100 / block.laneCount
              return (
                <Box
                  key={block.event.id}
                  position="absolute"
                  top={`${block.top}px`}
                  height={`${block.height}px`}
                  left={`calc(${CONTENT_LEFT}px + (100% - ${CONTENT_LEFT}px) * ${(laneWidth * block.lane) / 100})`}
                  width={`calc((100% - ${CONTENT_LEFT}px) * ${laneWidth / 100} - ${LANE_GAP}px)`}
                >
                  <CalendarEventCard event={block.event} overflowCount={block.overflowCount} />
                </Box>
              )
            })}
          </Box>

          {dayEvents.length === 0 && (
            <Text textAlign="center" color="rgba(255,255,255,0.55)" fontSize="13px" mt="12px" mb={0}>
              No events
            </Text>
          )}
        </>
      )}
    </Box>
  )
}

export default CalendarPanel
